import { useEffect, useRef, useState } from "react";
import { HERO } from "@/constants/testIds";
import { HERO_POSTER, HERO_VIDEO, HERO_VIDEO_FALLBACK, HERO_MAX_MB, warnIfOversize } from "@/config/media";

// Full-bleed hero background: autoplay, looping, muted, playsInline (iOS), no controls.
// The video fades in only once it can actually play, so there is no "static image
// then video" flash on load — the hero's dark background covers the brief buffer.
// The static skyline image is shown ONLY if the video fails / autoplay is blocked.
export const HeroBackground = () => {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) {
      setFailed(true);
      return;
    }
    v.muted = true; // iOS requires muted on the element for autoplay
    const attempt = v.play?.();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => setFailed(true)); // autoplay blocked -> fallback image
    }
    // Safety net: if the video never has enough data to play, show the image.
    const timer = setTimeout(() => {
      if (videoRef.current && videoRef.current.readyState < 3) setFailed(true);
    }, 7000);
    warnIfOversize([{ src: HERO_VIDEO, type: "video/webm" }], HERO_MAX_MB);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* Static skyline fallback — only rendered if the video can't play */}
      {failed && (
        <img
          data-testid="hero-video-fallback"
          src={HERO_POSTER}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
      )}

      {!failed && (
        <video
          ref={videoRef}
          data-testid={HERO.video}
          className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-700 ${
            ready ? "opacity-100" : "opacity-0"
          }`}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onCanPlay={() => setReady(true)}
          onPlaying={() => setReady(true)}
          onError={() => setFailed(true)}
        >
          <source src={HERO_VIDEO} type="video/webm" />
          <source src={HERO_VIDEO_FALLBACK} type="video/mp4" />
        </video>
      )}
    </>
  );
};
